import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button as BlueprintButton } from '@blueprintjs/core';
import { DateToLocal } from 'components/dates';
import { FormHelperText, FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { AlertConfirm } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import {
    buildFactText,
    buildFactValue,
    findTachoItemField,
    tachoItemsPageSize
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { TachoDebriefDialog } from 'pages/employee-reviews/complete/steps';
import { ClearedInfringementsNotice, RemovedInfringementIcon } from 'pages/employee-reviews/completed/snapshots';
import {
    getCurrentTachoAnswers,
    getTachoEditItemStatus,
    tachoEditItemStatuses
} from 'pages/employee-reviews/completed/edit/tachoInfringementsEdit';

const defaultArray = [];
const emptyTextValue = "-";
const occurredAtFormat = "DD/MM/YYYY HH:mm";
const returnedDateFormat = "DD MMM YYYY";
const noItemsMessage = "No infringements were debriefed on this review.";
const returnConfirmationMessage = "This infringement will appear on the driver's next review. Once saved, this cannot be undone.";

const headers = [
    "Code",
    "Date",
    "Infringement",
    "GFP cost",
    "Reason for fault",
    "Action taken",
    "Status",
    "Actions"
];

function buildTextValue(value) {
    if (value == null || value === "") {
        return emptyTextValue;
    }

    return value;
}

// Codes repeat across infringements, so each row's buttons are named by the code and the time the
// infringement occurred, formatted as the table shows it.
function buildItemName(item) {
    const code = buildFactText(item, "Code");
    const occurredAt = findTachoItemField(item, "OccurredAt")?.value;
    const occurredAtMoment = occurredAt == null || occurredAt === "" ? null : moment.utc(occurredAt);

    if (occurredAtMoment == null || !occurredAtMoment.isValid()) {
        return code;
    }

    return `${code} ${occurredAtMoment.local().format(occurredAtFormat)}`;
}

// Who returned an item and when is given in a tooltip, so the status column stays short. The
// tooltip renders no element of its own, so the status needs a wrapping span to hover.
function renderStatus(item, status) {
    if (status === tachoEditItemStatuses.returned) {
        return (
            <Tooltip
                content={(
                    <span className="nowrap">
                        By {item.returnedToOutstandingBy} on <DateToLocal format={returnedDateFormat}>{item.returnedToOutstandingDate}</DateToLocal>
                    </span>
                )}
            >
                <span>Returned to outstanding</span>
            </Tooltip>
        );
    }

    if (status === tachoEditItemStatuses.toBeReturned) {
        return <span>To be returned to outstanding</span>;
    }

    return <span>Reviewed</span>;
}

// The Tacho Infringements step of the edit wizard: the review's reviewed infringements, each of
// which can have its reason for fault, action taken and emergency flag corrected or be returned to
// outstanding, then the topic's comment. Infringements cleared by the review are shown only as the completed
// review shows them, and cannot be changed. Nothing is sent until the edit is saved.
export function TachoInfringementsEditStep(
    {
        state = null,
        topicEntry = null,
        loading = false,
        onChange
    }
) {

    const items = state?.items ?? defaultArray;
    const debrief = state?.debrief ?? null;
    const pageSize = state?.pageSize ?? tachoItemsPageSize;
    const debriefItem = items.find((item) => item.reviewItemId === debrief?.reviewItemId) ?? null;
    const pendingReturnItem = items.find((item) => item.reviewItemId === state?.pendingReturnId) ?? null;

    function onPagingChange(newPageNumber, newPageSize) {
        onChange({ pageNumber: newPageNumber, pageSize: newPageSize });
    }

    function onOpenDebrief(item) {
        onChange({
            debrief: {
                reviewItemId: item.reviewItemId,
                ...getCurrentTachoAnswers(state, item)
            }
        });
    }

    function onDebriefFieldChange(fieldName, value) {
        onChange((topicState) => ({ debrief: { ...topicState.debrief, [fieldName]: value } }));
    }

    function onDebriefCancel() {
        onChange({ debrief: null });
    }

    function onDebriefSave() {
        onChange((topicState) => ({
            records: {
                ...topicState.records,
                [topicState.debrief.reviewItemId]: {
                    reasonForFault: topicState.debrief.reasonForFault,
                    actionTaken: topicState.debrief.actionTaken,
                    emergency: topicState.debrief.emergency === true
                }
            },
            debrief: null
        }));
    }

    function onReturnRequested(item) {
        onChange({ pendingReturnId: item.reviewItemId });
    }

    function onReturnCancel() {
        onChange({ pendingReturnId: null });
    }

    // A correction made to the item is kept, so it applies again if the return is undone. While the
    // item is marked to be returned only the return is sent.
    function onReturnConfirm() {
        onChange((topicState) => ({
            returns: { ...topicState.returns, [topicState.pendingReturnId]: true },
            pendingReturnId: null
        }));
    }

    // Nothing is sent until the edit is saved, so a return chosen on this page is undone without asking.
    function onUndoReturn(item) {
        onChange((topicState) => {
            const remainingReturns = { ...topicState.returns };
            delete remainingReturns[item.reviewItemId];

            return { returns: remainingReturns };
        });
    }

    function onCommentChange(event) {
        onChange({ comment: event.target.value });
    }

    // A reviewed item can be corrected or returned, and a return chosen on this page can be undone.
    // An item returned by an earlier save offers nothing.
    function renderActions(item, status, itemName) {
        const reference = item.reviewItemId;

        if (status === tachoEditItemStatuses.toBeReturned) {
            return (
                <span className="inline-items nowrap">
                    <Tooltip content="Keep as reviewed">
                        <BlueprintButton
                            id={`tacho-infringement-undo-return-${reference}`}
                            icon="cross"
                            variant="minimal"
                            aria-label={`Keep ${itemName} as reviewed`}
                            onClick={() => onUndoReturn(item)}
                        />
                    </Tooltip>
                </span>
            );
        }

        if (status !== tachoEditItemStatuses.reviewed) {
            return null;
        }

        return (
            <span className="inline-items nowrap">
                <Tooltip content="Edit">
                    <BlueprintButton
                        id={`tacho-infringement-edit-${reference}`}
                        icon="edit"
                        variant="minimal"
                        aria-label={`Edit ${itemName}`}
                        onClick={() => onOpenDebrief(item)}
                    />
                </Tooltip>
                {/* An infringement removed from the driver's record can never appear on a
                    later review, so it cannot be returned to outstanding. */}
                <ShowHide
                    evaluator={item.infringementDeleted !== true}
                    show={(
                        <Tooltip content="Return to outstanding">
                            <BlueprintButton
                                id={`tacho-infringement-return-${reference}`}
                                icon="undo"
                                variant="minimal"
                                aria-label={`Return ${itemName} to outstanding`}
                                onClick={() => onReturnRequested(item)}
                            />
                        </Tooltip>
                    )}
                />
            </span>
        );
    }

    // The table re-seats itself on startPage whenever its row count moves, so the page is driven
    // from here and clamped to the pages the items fill.
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const activePage = Math.min(state?.pageNumber ?? 1, totalPages);
    const pageStart = (activePage - 1) * pageSize;
    const pageItems = items.slice(pageStart, pageStart + pageSize);

    const rowData = pageItems.map((item) => {
        const reference = item.reviewItemId;
        const status = getTachoEditItemStatus(state, item);
        const answers = getCurrentTachoAnswers(state, item);
        const itemName = buildItemName(item);

        return [
            (
                <span key={`tacho-infringement-code-${reference}`} className="inline-items nowrap">
                    <RemovedInfringementIcon item={item} />
                    <span>{buildFactValue(item, "Code")}</span>
                </span>
            ),
            (
                <span key={`tacho-infringement-occurred-${reference}`} className="nowrap">{buildFactValue(item, "OccurredAt")}</span>
            ),
            (
                <Fragment key={`tacho-infringement-type-${reference}`}>{buildFactValue(item, "InfringementType")}</Fragment>
            ),
            (
                <span key={`tacho-infringement-gfp-cost-${reference}`} className="nowrap">{buildFactValue(item, "GfpCost")}</span>
            ),
            (
                <Fragment key={`tacho-infringement-reason-${reference}`}>{buildTextValue(answers.reasonForFault)}</Fragment>
            ),
            (
                <Fragment key={`tacho-infringement-action-${reference}`}>{buildTextValue(answers.actionTaken)}</Fragment>
            ),
            (
                <span key={`tacho-infringement-status-${reference}`} className="nowrap">{renderStatus(item, status)}</span>
            ),
            (
                <Fragment key={`tacho-infringement-actions-${reference}`}>{renderActions(item, status, itemName)}</Fragment>
            )
        ];
    });

    return (
        <div>
            <h2>{topicEntry?.topicName ?? "Tacho Infringements"}</h2>
            <FormHelperText loading={loading}>Correct the infringements debriefed on this review, or return them to outstanding.</FormHelperText>
            <ClearedInfringementsNotice
                clearedItems={state?.clearedItems ?? defaultArray}
                listedItemCount={items.length}
                itemsClearedBefore={state?.itemsClearedBefore ?? null}
                loading={loading}
            />
            <ShowHide
                evaluator={!loading && items.length === 0}
                show={(
                    <p>{noItemsMessage}</p>
                )}
                hide={(
                    <ListingTable
                        id="tacho-infringement-items"
                        loadingData={loading}
                        headers={headers}
                        data={rowData}
                        totalRecordCount={items.length}
                        showPerPage={true}
                        pageSize={pageSize}
                        startPage={activePage}
                        onPagingChange={onPagingChange}
                        rowClickEnabled={false}
                    />
                )}
            />
            <div className="spacer-top">
                <FormTextArea
                    id="tacho-infringements-comment"
                    headingText="Overall comments"
                    placeholder="Add any overall comments for this topic..."
                    value={state?.comment ?? ""}
                    loading={loading}
                    fill
                    onChange={onCommentChange}
                />
            </div>
            <TachoDebriefDialog
                item={debriefItem}
                debrief={debrief}
                saveText="Save"
                onChange={onDebriefFieldChange}
                onSave={onDebriefSave}
                onCancel={onDebriefCancel}
            />
            <AlertConfirm
                isOpen={pendingReturnItem != null}
                title="Return to outstanding"
                confirmButtonText="Return to outstanding"
                onConfirm={onReturnConfirm}
                onCancel={onReturnCancel}
            >
                <p>{returnConfirmationMessage}</p>
            </AlertConfirm>
        </div>
    );
}

TachoInfringementsEditStep.propTypes = {
    state: PropTypes.object,
    topicEntry: PropTypes.object,
    loading: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};
